import { Typography } from 'antd';
import type { Repository } from './types';

const { Text } = Typography;

interface RepositoryDetailsProps {
  repo: Repository;
  riskAssessment?: string | null;
  topRemediations?: string | null;
}

// Mirrors garnet-fe's SelectedRepo.tsx#parseRiskAssessment: split on
// newline, trim, keep all non-empty lines (no `-` prefix requirement,
// distinct from utils/textFormatting#parseBulletPoints which only keeps
// `- `-prefixed lines).
const parseLines = (text?: string | null): string[] => {
  if (!text) return [];
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);
};

const RepositoryDetails = ({ repo, riskAssessment, topRemediations }: RepositoryDetailsProps) => {
  const riskAssessmentItems = parseLines(riskAssessment);
  const remediationItems = parseLines(topRemediations);

  return (
    <div className="text-sm leading-[22px]">
      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Description</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
          {repo.description || 'No description available.'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Repository Stars</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
          {repo.stars ? Number(repo.stars).toLocaleString() : 'N/A'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Risk</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
          {repo.risk?.[0] || 'N/A'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Resolved Version</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text font-mono">
          {repo.lastCommitHash?.[0] || 'N/A'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading"># Tools</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
          {repo._tools?.[0] || 'N/A'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Commit Hash</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
          {repo.lastCommitHash?.[0] || 'N/A'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Size (GB)</Text>
        <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
          {repo.sizeGb ? parseFloat(repo.sizeGb).toFixed(4) : 'N/A'}
        </Text>
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Risk Assessment</Text>
        {riskAssessmentItems.length > 0 ? (
          <ul className="list-disc list-inside text-theme-text">
            {riskAssessmentItems.map((item, index) => (
              <li key={index}>
                <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
                  {item}
                </Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">No risk assessment available</Text>
        )}
      </div>

      <div className="mb-6">
        <Text strong className="text-sm leading-[22px] block mb-2 !text-theme-heading">Top Remediations</Text>
        {remediationItems.length > 0 ? (
          <ul className="list-disc list-inside text-theme-text">
            {remediationItems.map((item, index) => (
              <li key={index}>
                <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">
                  {item}
                </Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text type="secondary" className="text-sm leading-[22px] !text-theme-text">No remediations available</Text>
        )}
      </div>
    </div>
  );
};

export default RepositoryDetails;
